import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import GlobalModal from "components/dialog/GlobalModal";
import PopupDialog from "components/dialog/PopupDialog";
import { useAuth } from "context/AuthProvider";
import { MyConst } from "data/MyConst";
import useAxiosFunction from "hooks/useAxiosFunction";
import Footer from "layouts/Footer";
import Header from "layouts/Header";
import Sidebar from "layouts/Sidebar";
import Topbar from "layouts/Topbar";
import { loadTree } from "services/loadtreeview";
import { LoadingContent } from "utils/MyUtils";
import MakeRouter from "utils/MakeRouter";

export default function AdminLayout() {
  const { user, replaceUser, isVersionObsolete } = useAuth();
  const location = useLocation();
  const loading = useSelector((state) => state.utility.loading);
  const showMsgBox = useSelector((state) => state.utility.showMsgBox);
  const hasBootstrapData = Boolean(
    user?.token &&
      Array.isArray(user?.menus) &&
      Array.isArray(user?.permissions),
  );
  const [bootstrapping, setBootstrapping] = useState(!hasBootstrapData);
  const [bootstrapError, setBootstrapError] = useState("");
  const [bootstrapSlow, setBootstrapSlow] = useState(false);
  const [serverVersion, setServerVersion] = useState("");
  const [, , , axiosFetch] = useAxiosFunction();
  const lastBootstrapKeyRef = useRef("");
  const backendVersion = serverVersion || user?.version || user?.app_info?.version || "-";
  const versionObsolete = Boolean(
    serverVersion
      ? MyConst.VERSION !== serverVersion
      : isVersionObsolete(),
  );

  const reloadApplication = async () => {
    if (window?.caches?.keys) {
      try {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((key) => window.caches.delete(key)));
      } catch (error) {
        // Cache API may be unavailable depending on browser policy; reload remains the main action.
      }
    }
    window.localStorage.removeItem("user");
    window.sessionStorage.clear();
    window.location.replace("/login");
  };

  const activeTitle = useMemo(() => {
    if (!user) return "";
    if (String(user?.groupname || "").toUpperCase() === "PERSONAL") {
      return (
        user?.personal_koni_kabkota_name ||
        user?.pdf_header?.context_name ||
        user?.group_description ||
        user?.groupname ||
        MyConst.SYS_NAME
      );
    }
    if (String(user?.context_type || "").toUpperCase() === "KEJUARAAN") {
      return (
        user?.active_context?.context_name ||
        user?.active_context?.label ||
        user?.groupname ||
        MyConst.SYS_NAME
      );
    }
    return (
      user?.active_context?.context_name ||
      user?.active_context?.label ||
      user?.groupname ||
      MyConst.SYS_NAME
    );
  }, [user]);

  useEffect(() => {
    document.body.className = "hold-transition sidebar-mini layout-fixed";
    loadTree();
  }, []);

  const fetchServerVersion = React.useCallback(async () => {
    const resp = await axiosFetch({
      method: "GET",
      url: "/version",
      timeout: 10000,
    });

    if (resp?.data?.version) {
      setServerVersion(resp.data.version);
    }
  }, [axiosFetch]);

  useEffect(() => {
    fetchServerVersion();
    const intervalId = window.setInterval(fetchServerVersion, 15 * 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchServerVersion]);

  useEffect(() => {
    if (location.pathname === "/") {
      fetchServerVersion();
    }
  }, [fetchServerVersion, location.pathname]);

  useEffect(() => {
    let cancelled = false;

    async function fetchBootstrap() {
      if (!user?.token) {
        lastBootstrapKeyRef.current = "";
        setBootstrapping(false);
        return;
      }

      const bootstrapKey = `${user.uid || ""}:${user.id_user_group || ""}:${user.version || ""}:${MyConst.BOOTSTRAP_SCHEMA_VERSION || ""}`;
      if (
        hasBootstrapData &&
        (lastBootstrapKeyRef.current === "" ||
          user.bootstrap_ready_key === bootstrapKey)
      ) {
        lastBootstrapKeyRef.current = bootstrapKey;
        setBootstrapping(false);
        return;
      }
      if (lastBootstrapKeyRef.current === bootstrapKey) {
        setBootstrapping(false);
        return;
      }

      setBootstrapping(true);
      setBootstrapSlow(false);

      const slowTimer = window.setTimeout(() => {
        if (!cancelled) {
          setBootstrapSlow(true);
        }
      }, 5000);

      try {
        const resp = await axiosFetch({
          method: "GET",
          url: "/app/bootstrap",
          auth: true,
          authToken: user.token,
          timeout: 15000,
        });

        if (cancelled) return;

        if (resp?.data?.user) {
          await replaceUser({
            ...user,
            ...resp.data.user,
            menus: resp.data.menus || [],
            permissions: resp.data.permissions || [],
            settings: resp.data.settings || {},
            personal_fields: resp.data.personal_fields || [],
            app_info: resp.data.app || {},
            bootstrap_ready_key: bootstrapKey,
          });
          lastBootstrapKeyRef.current = bootstrapKey;
          setBootstrapError("");
        } else {
          lastBootstrapKeyRef.current = "";
          setBootstrapError(resp?.message || "Bootstrap aplikasi gagal dimuat.");
        }
      } finally {
        window.clearTimeout(slowTimer);
        if (!cancelled) {
          setBootstrapSlow(false);
          setBootstrapping(false);
        }
      }
    }

    fetchBootstrap();

    return () => {
      cancelled = true;
    };
  }, [
    axiosFetch,
    hasBootstrapData,
    replaceUser,
    user?.id_user_group,
    user?.bootstrap_ready_key,
    user?.uid,
    user?.version,
  ]);

  useEffect(() => {
    if (!bootstrapping) {
      loadTree();
    }
  }, [bootstrapping, user?.menus]);

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user?.must_change_password && location.pathname !== "/ubah-password") {
    return <Navigate to="/ubah-password" replace />;
  }

  if (
    String(user?.groupname || "").toUpperCase() === "PERSONAL" &&
    user?.is_complete === false &&
    location.pathname !== "/profil-saya" &&
    location.pathname !== "/ubah-password"
  ) {
    return <Navigate to="/profil-saya" replace />;
  }

  if (bootstrapping) {
    return (
      <div className="wrapper">
        <Header imgloading={MyConst.LOADING_FILE} />
        <LoadingContent />
        {bootstrapSlow ? (
          <div className="text-center text-muted" style={{ marginTop: -120 }}>
            Memuat aplikasi lebih lama dari biasanya. Jika tetap berhenti di sini, silakan refresh halaman.
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="wrapper">
      <Header imgloading={MyConst.LOADING_FILE} />
      <Topbar
        title={activeTitle}
        contexts={user.contexts || []}
        activeContextId={user.id_user_group}
        bootstrapError={bootstrapError}
      />
      <Sidebar
        user={user}
        menus={user.menus}
        username={user.username}
        aliasname={user.unit}
        foto={user.photo}
        fotoLocation={user.photo_location}
      />
      <MakeRouter menus={user.menus} />
      <Footer
        web={MyConst.WEB}
        version={
          versionObsolete
            ? `${MyConst.VERSION} (kadaluarsa)`
            : serverVersion || user?.app_info?.version || MyConst.VERSION
        }
      />
      {loading ? <LoadingContent /> : null}
      <GlobalModal {...showMsgBox} />
      <PopupDialog
        title="Informasi"
        size="md"
        show={!bootstrapping && versionObsolete}
        allowClose={false}
        doLabel="Update Aplikasi"
        doHandle={reloadApplication}
      >
        <p className="mb-0">
          Pengguna yang terhormat, saat ini telah tersedia sistem terbaru dengan versi{" "}
          {backendVersion}. Anda masih menggunakan versi {MyConst.VERSION}, silakan
          perbaharui dengan melakukan clear cache atau menekan tombol CTRL + F5. Setelah itu
          silakan logout dan login kembali. Terima kasih.
        </p>
      </PopupDialog>
    </div>
  );
}
